import * as React from 'react';
import FullWidthContainer from "components/FullWidthContainer";
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import zparse from 'components/zparse';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-mui';
import { useSpring, animated } from "@react-spring/web";

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import icon from 'images/zeek.svg';
import chevron from 'images/right-chevron-blue600.svg';
import paperPlane from 'images/icons/paperPlane.svg';

const Zeek = ({active, setActive, ...props}) => {
	const initialHistory = React.useMemo(() => [
		{assistant: '<p>What can I help you with?</p>'},
	], []);
	const { executeRecaptcha } = useGoogleReCaptcha();
	// const [active, setActive] = React.useState(props.active||true);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [history, setHistory] = React.useState(initialHistory);
	const { className } = props;
	const bodyRef = React.useRef();
	const formikRef = React.useRef();

	const initialValues = {
		question: '',
	}

	const handleSubmit = React.useCallback(async ({question}, {setSubmitting, resetForm}) => {
		if (!executeRecaptcha) {
			return null;
		}
		executeRecaptcha('zeek')
			.then(token => {
				if (question === '') {
					setHistory([
						...history,
						{assistant: '<p>Go ahead and ask me anything.</p>'},
					]);
					return;
				}
				setIsSubmitting(true);
				resetForm();
				setHistory([
					...history,
					{user: `<p>${question}</p>`},
				]);
				const options = {
					credentials: 'include',
				};
				fetch(`${process.env.GATSBY_ZMS_API_URL}chat/question/?query=${encodeURIComponent(question)}&recaptcha=${token}`, options)
					.then((response) => response.json())
					.then((json) => {
						setIsSubmitting(false);
						if (json.data.length > 0) {
							setHistory([
								...history,
								{user: `<p>${question}</p>`},
								{assistant: json.data[0].contentHTML},
							])
							if (typeof window !== 'undefined') {
								window.dataLayer = window.dataLayer||[];
								window.dataLayer.push({
									event: 'chat_submit',
									formID: 'chatbot',
									success: true,
									...props,
									response: json.data[0].content,
								});
							}
						} else {
							setHistory([
								...history,
								{user: `<p>${question}</p>`},
								{assistant: `<p>I'm sorry, apparently I had some trouble understanding and answering your question. Perhaps you could rephrase it to help me out.</p>`},
							])
						}
					})
					.catch(function (err) {
						setHistory([
							...history,
							{user: `<p>${question}</p>`},
							{assistant: `<p>I'm sorry, apparently I had some trouble understanding and answering your question. Perhaps you could rephrase it to help me out.</p>`},
						])
						if (typeof window !== 'undefined') {
							window.dataLayer = window.dataLayer||[];
							window.dataLayer.push({
								event: 'chat_error',
								formID: 'chatbot',
								error: err,
								...props,
							});
						}
						setIsSubmitting(false);
					})
				;
			})
			.catch(err => {
				console.log(err);
			})
		;
	}, [executeRecaptcha, history, setHistory, props]);

	const handleKeyUp = (evt) => {
		if (evt.key === 'Enter' && formikRef.current)formikRef.current.handleSubmit();
	}

	const reset = React.useCallback(() => {
		setHistory(initialHistory);
		formikRef.current?.resetForm();
		const options = {
			credentials: 'include',
		};
		fetch(`${process.env.GATSBY_ZMS_API_URL}chat/clearHistory/`, options);
	}, [initialHistory]);

	const closeZeek = React.useCallback(() => {
		setActive(false);
	}, [setActive]);

	React.useEffect(() => {
		if (!!bodyRef.current === false)return;
		if (!className.includes('mobile'))return;
		const body = bodyRef.current;
		const handler = (evt) => {
			if (evt.target.matches('a'))closeZeek();
		}
		if (active) {
			body.addEventListener('click', handler);
		} else {
			body.removeEventListener('click', handler);
		}
		return () => {
			body.removeEventListener('click', handler);
		}
	}, [active, closeZeek, className]);

	React.useEffect(() => {
		if (!!bodyRef.current === false)return;
		const histories = bodyRef.current.querySelectorAll('.Zeek-log');
		if (histories.length === 0)return;
		const last = histories[histories.length-1];
		last.scrollIntoView();
	}, [history]);

	const style = {
		...(props.menuHeight ? {
			height: `${props.menuHeight}px`,
		} : {}),
	}

	const animateFrom = React.useMemo(() => {
		return {
			x: 0,
		}
	}, []);

	const animateTo = {
		x: -30,
	}

	const springs = {
		from: {
			...animateFrom,
		},
		to: {
			...animateTo,
		},
	}

	const [animation, api] = useSpring(() => {
		return {
			...springs,
			pause: true,
			duration: 200,
			config: {
				friction: 15,
			},
		}
	});

	const resetAnimation = React.useCallback((pause = false) => {
		api.set(animateFrom);
		if (pause)api.pause();
		else api.resume();
	}, [api, animateFrom]);

	React.useEffect(() => {
		if (active)resetAnimation(active);
	}, [active, resetAnimation]);


	const handleMouseOver = () => {
		if (!active) {
			api.start({
				pause: false,
				...springs
			});
		}
	}

	const handleMouseOut = () => {
		if (!active) {
			api.start({
				to: animateFrom,
				from: animateTo,
			});
		}
	}

	const handleClick = () => {
		setActive(!active);
	}

	// console.log(`active: ${active}`);

	// api.set(animateFrom);

	return (
		<FullWidthContainer sx={style} className={`Zeek-root ${active ? 'Zeek-active' : ''} ${history.length > 1 ? 'Zeek-history' : ''} ${className||''}`}>
			<animated.div style={animation}>
				<div className="Zeek-header">
					<div className="Zeek-helmet">
						<Button variant="invisible" disableRipple={true} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut} onClick={handleClick}><img src={icon} alt="Toggle Chat"/></Button>
					</div>
					<div className="Zeek-title">
						<Typography component="div" variant="h4">Welcome to<br/>Mission Control</Typography>
					</div>
					<div className="Zeek-minimize">
						<Button variant="invisible" disableRipple={true} onClick={() => setActive(false)}><img src={chevron} alt="Minimize" /></Button>
					</div>
				</div>
			</animated.div>
			<div ref={bodyRef} className="Zeek-body" role="log" aria-live="polite">
				{ [...history].map((message, index) => <div key={index} className={`Zeek-log Zeek-${Object.keys(message).join('')}`}>{zparse(Object.values(message).join(''))}</div>)}
				{ isSubmitting && <div className="Zeek-loader" /> }
				{ history.length > 1 && active && !isSubmitting && <div className="Zeek-clear"><Button variant="invisible" disableRipple={true} onClick={() => reset()}>Clear Chat</Button></div> }
			</div>
			<div className="Zeek-footer">
				<Formik onSubmit={handleSubmit} initialValues={initialValues} innerRef={formikRef}>
					{({ submitForm, isSubmitting }) => (
						<Form>
							<Field component={TextField} className="Zeek-question" disabled={isSubmitting} autoComplete={"false"} fullWidth label="Write a message..." id={`zeek_question_${className}`} name="question" variant="outlined" onKeyUp={handleKeyUp} />
							<Button variant="invisible" disabled={isSubmitting} onClick={submitForm}><img src={paperPlane} alt="Send Question" /></Button>
						</Form>
					)}
				</Formik>
			</div>
		</FullWidthContainer>
	)
}

export default Zeek;